"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { i18n, localeLabels, Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

type FooterCopy = {
    columns: {
        product: string;
        account: string;
        support: string;
        legal: string;
    };
    links: {
        courses: string;
        howItWorks: string;
        pricing: string;
        updates: string;
        login: string;
        signup: string;
        progress: string;
        restore: string;
        help: string;
        contact: string;
        feedback: string;
        report: string;
        privacy: string;
        terms: string;
        refund: string;
        cookies: string;
    };
    language: string;
    neutralLine: string;
};

const footerCopyByLocale = defineLocaleRecord<FooterCopy>({
    en: {
        columns: {
            product: "Product",
            account: "Account",
            support: "Support",
            legal: "Legal",
        },
        links: {
            courses: "Courses",
            howItWorks: "How it works",
            pricing: "Pricing",
            updates: "Updates",
            login: "Log in",
            signup: "Sign up",
            progress: "My progress",
            restore: "Restore access",
            help: "Help / FAQ",
            contact: "Contact",
            feedback: "Feedback",
            report: "Report a problem",
            privacy: "Privacy Policy",
            terms: "Terms and Conditions",
            refund: "Refund Policy",
            cookies: "Cookies",
        },
        language: "Language",
        neutralLine: "Built for focused learning.",
    },
    fr: {
        columns: {
            product: "Produit",
            account: "Compte",
            support: "Support",
            legal: "Legal",
        },
        links: {
            courses: "Parcours",
            howItWorks: "Comment ca marche",
            pricing: "Tarifs",
            updates: "Mises a jour",
            login: "Connexion",
            signup: "Inscription",
            progress: "Ma progression",
            restore: "Restaurer l'acces",
            help: "Aide / FAQ",
            contact: "Contact",
            feedback: "Retour",
            report: "Signaler un probleme",
            privacy: "Confidentialite",
            terms: "Conditions d'utilisation",
            refund: "Politique de remboursement",
            cookies: "Cookies",
        },
        language: "Langue",
        neutralLine: "Concu pour un apprentissage concentre.",
    },
    ru: {
        columns: {
            product: "Продукт",
            account: "Аккаунт",
            support: "Поддержка",
            legal: "Правовая информация",
        },
        links: {
            courses: "Курсы",
            howItWorks: "Как это работает",
            pricing: "Тарифы",
            updates: "Обновления",
            login: "Войти",
            signup: "Регистрация",
            progress: "Мой прогресс",
            restore: "Восстановить доступ",
            help: "Помощь / FAQ",
            contact: "Контакты",
            feedback: "Обратная связь",
            report: "Сообщить о проблеме",
            privacy: "Политика конфиденциальности",
            terms: "Условия использования",
            refund: "Политика возврата",
            cookies: "Cookies",
        },
        language: "Язык",
        neutralLine: "Создано для сфокусированного обучения.",
    },
    uk: {
        columns: {
            product: "Продукт",
            account: "Акаунт",
            support: "Підтримка",
            legal: "Юридична інформація",
        },
        links: {
            courses: "Курси",
            howItWorks: "Як це працює",
            pricing: "Тарифи",
            updates: "Оновлення",
            login: "Увійти",
            signup: "Реєстрація",
            progress: "Мій прогрес",
            restore: "Відновити доступ",
            help: "Допомога / FAQ",
            contact: "Контакт",
            feedback: "Зворотний зв'язок",
            report: "Повідомити про проблему",
            privacy: "Політика конфіденційності",
            terms: "Умови використання",
            refund: "Політика повернення",
            cookies: "Cookies",
        },
        language: "Мова",
        neutralLine: "Створено для сфокусованого навчання.",
    },
    de: {
        columns: {
            product: "Produkt",
            account: "Konto",
            support: "Support",
            legal: "Rechtliches",
        },
        links: {
            courses: "Kurse",
            howItWorks: "Wie es funktioniert",
            pricing: "Preise",
            updates: "Updates",
            login: "Anmelden",
            signup: "Registrieren",
            progress: "Mein Fortschritt",
            restore: "Zugang wiederherstellen",
            help: "Hilfe / FAQ",
            contact: "Kontakt",
            feedback: "Feedback",
            report: "Problem melden",
            privacy: "Datenschutz",
            terms: "Nutzungsbedingungen",
            refund: "Rueckerstattungsrichtlinie",
            cookies: "Cookies",
        },
        language: "Sprache",
        neutralLine: "Gebaut fuer fokussiertes Lernen.",
    },
    es: {
        columns: {
            product: "Producto",
            account: "Cuenta",
            support: "Soporte",
            legal: "Legal",
        },
        links: {
            courses: "Cursos",
            howItWorks: "Como funciona",
            pricing: "Precios",
            updates: "Actualizaciones",
            login: "Entrar",
            signup: "Registro",
            progress: "Mi progreso",
            restore: "Restaurar acceso",
            help: "Ayuda / FAQ",
            contact: "Contacto",
            feedback: "Feedback",
            report: "Reportar un problema",
            privacy: "Politica de privacidad",
            terms: "Terminos del servicio",
            refund: "Politica de reembolso",
            cookies: "Cookies",
        },
        language: "Idioma",
        neutralLine: "Hecho para aprendizaje enfocado.",
    },
    it: {
        columns: {
            product: "Prodotto",
            account: "Account",
            support: "Supporto",
            legal: "Legale",
        },
        links: {
            courses: "Corsi",
            howItWorks: "Come funziona",
            pricing: "Prezzi",
            updates: "Aggiornamenti",
            login: "Accedi",
            signup: "Registrazione",
            progress: "I miei progressi",
            restore: "Ripristina accesso",
            help: "Aiuto / FAQ",
            contact: "Contatto",
            feedback: "Feedback",
            report: "Segnala un problema",
            privacy: "Informativa sulla privacy",
            terms: "Termini di servizio",
            refund: "Politica di rimborso",
            cookies: "Cookie",
        },
        language: "Lingua",
        neutralLine: "Costruito per un apprendimento focalizzato.",
    },
    pt: {
        columns: {
            product: "Produto",
            account: "Conta",
            support: "Suporte",
            legal: "Legal",
        },
        links: {
            courses: "Cursos",
            howItWorks: "Como funciona",
            pricing: "Precos",
            updates: "Novidades",
            login: "Entrar",
            signup: "Cadastrar",
            progress: "Meu progresso",
            restore: "Restaurar acesso",
            help: "Ajuda / FAQ",
            contact: "Contato",
            feedback: "Feedback",
            report: "Relatar um problema",
            privacy: "Politica de Privacidade",
            terms: "Termos e Condicoes",
            refund: "Politica de Reembolso",
            cookies: "Cookies",
        },
        language: "Idioma",
        neutralLine: "Feito para aprendizado focado.",
    },
});

const localeNames = defineLocaleRecord<string>({
    en: "English",
    fr: "Français",
    ru: "Русский",
    uk: "Українська",
    de: "Deutsch",
    es: "Español",
    it: "Italiano",
    pt: "Portugues",
});

type EpilogueFooterProps = {
    lang: Locale;
    enabledLocales: readonly Locale[];
    showPaymentsFeature?: boolean;
};

export function EpilogueFooter({ lang, enabledLocales, showPaymentsFeature = true }: EpilogueFooterProps) {
    const copy = footerCopyByLocale[lang];
    const router = useRouter();
    const pathname = usePathname();

    const switchLocale = (target: Locale) => {
        if (!pathname) {
            router.push(`/${target}`);
            return;
        }

        if (pathname === "/") {
            router.push(`/${target}`);
            return;
        }

        const segments = pathname.split("/");
        const currentFirst = segments[1];

        if (i18n.locales.includes(currentFirst as Locale)) {
            segments[1] = target;
            router.push(segments.join("/") || `/${target}`);
            return;
        }

        router.push(`/${target}`);
    };

    const productLinks = [
        { label: copy.links.courses, href: `/${lang}/courses` },
        { label: copy.links.howItWorks, href: `/${lang}#when-needed` },
        { label: copy.links.updates, href: `/${lang}/updates` },
    ];
    if (showPaymentsFeature) {
        productLinks.splice(2, 0, { label: copy.links.pricing, href: `/${lang}#access-plans` });
    }

    const accountLinks = [
        { label: copy.links.login, href: `/${lang}/login` },
        { label: copy.links.signup, href: `/${lang}/signup` },
        { label: copy.links.progress, href: `/${lang}/hub` },
        { label: copy.links.restore, href: `/${lang}/login?restore=1` },
    ];

    const supportLinks = [
        { label: copy.links.help, href: `/${lang}/help` },
        { label: copy.links.contact, href: "mailto:support@zipvibe.com" },
        { label: copy.links.feedback, href: "mailto:feedback@zipvibe.com" },
        { label: copy.links.report, href: "mailto:support@zipvibe.com?subject=Report%20a%20problem" },
    ];

    const legalLinks = [
        { label: copy.links.privacy, href: `/${lang}/privacy` },
        { label: copy.links.terms, href: `/${lang}/terms` },
        { label: copy.links.refund, href: `/${lang}/refund-policy` },
        { label: copy.links.cookies, href: `/${lang}/cookies` },
    ];

    const columns = [
        { title: copy.columns.product, links: productLinks },
        { title: copy.columns.account, links: accountLinks },
        { title: copy.columns.support, links: supportLinks },
        { title: copy.columns.legal, links: legalLinks },
    ];

    return (
        <footer className="border-t border-[#d8cdb9] bg-[#e8e0d1] text-[#3f514b]">
            <div className="container max-w-6xl px-4 py-14 md:py-16">
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
                    {columns.map((column) => (
                        <div key={column.title}>
                            <h3 className="text-xs font-semibold uppercase tracking-[0.11em] text-[#657770]">
                                {column.title}
                            </h3>
                            <ul className="mt-4 space-y-2 text-sm">
                                {column.links.map((item) => (
                                    <li key={`${column.title}-${item.label}`}>
                                        {item.href.startsWith("mailto:") ? (
                                            <a href={item.href} className="transition-colors hover:text-[#1f2d29]">
                                                {item.label}
                                            </a>
                                        ) : (
                                            <Link href={item.href} className="transition-colors hover:text-[#1f2d29]">
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-10 border-t border-[#d8cdb9] pt-5">
                    <div className="flex flex-col gap-3 text-xs text-[#6d7d77] sm:flex-row sm:items-center sm:justify-between">
                        <span>© ZIPVIBE, 2026</span>
                        <label className="inline-flex items-center gap-2">
                            <span>{copy.language}</span>
                            <select
                                value={lang}
                                onChange={(event) => switchLocale(event.target.value as Locale)}
                                className="h-8 rounded-md border border-[#cdbfa7] bg-[#f5efe3] px-2 text-sm text-[#2f403b] outline-none focus:border-[#0f766e]"
                                aria-label={copy.language}
                            >
                                {enabledLocales.map((code) => (
                                    <option key={code} value={code}>
                                        {localeLabels[code] ?? localeNames[code]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                <p className="mt-5 text-center text-[0.7rem] text-[#7d8a85]">{copy.neutralLine}</p>
            </div>
        </footer>
    );
}
